/**
 * Regroup Anvil singles into five-packs on the cart page.
 *
 * The product page composes an order correctly — `anvilOrderLines` decides how
 * many packs and how many singles, and the block adds exactly those lines. The
 * CART page has its own quantity stepper, and it does not know any of that: it
 * edits one line in place. So a buyer who adds six (one pack, one single) and
 * then raises the single to six has eleven devices billed as one pack plus six
 * singles, $1,824, when eleven cost $1,679.
 *
 * That error is an OVERCHARGE, which is the direction worth writing code for.
 *
 * Narrow on purpose. It only acts when a SINGLES line has reached five, which
 * is the only way the cart can drift from the ladder — three singles beside a
 * pack is already right, and so is any remainder under five. It never merges
 * across lines, so the colour on each line is unambiguous and rides onto the
 * pack it becomes.
 *
 * The real fix is a Cart Transform function, which would hold for every path
 * into the cart rather than just this page. That needs `shopify app deploy`,
 * which is blocked on the unfinished function in #1461.
 */
(function () {
  if (!window.location.pathname.startsWith('/cart')) return;

  var SINGLE_HANDLES = ['anvil', 'anvil-solo'];
  var PACK_HANDLE = 'anvil-5-pack';
  var PACK_UNITS = 5;
  // Set just before the reload a regroup triggers, and consumed by the page
  // load that reload produces, which therefore skips once. That pass must find
  // nothing left to do anyway; if a bug ever made it think otherwise, this is
  // what stops a reload loop. Every OTHER cart page load runs. The flag used
  // to be set on every run and never cleared, which in sessionStorage meant
  // one pass per TAB: the cart a buyer lands on after adding six, with nothing
  // to regroup yet, switched it off for the edit it exists to catch.
  var RAN_KEY = 'ruck-cart-packs-ran';

  function postJson(path, payload) {
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart ' + res.status);
      return res.json();
    });
  }

  function run() {
    var justRegrouped;
    try {
      justRegrouped = sessionStorage.getItem(RAN_KEY);
      sessionStorage.removeItem(RAN_KEY);
    } catch {
      // Private mode or blocked storage. Proceed: the work is idempotent, and
      // the reload below only happens when something actually changed.
      justRegrouped = null;
    }
    if (justRegrouped) return;

    fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var overfull = (cart.items || []).filter(function (line) {
          return SINGLE_HANDLES.indexOf(line.handle) !== -1 && line.quantity >= PACK_UNITS;
        });
        if (overfull.length === 0) return null;

        // The pack's variant, looked up rather than hardcoded, so this file
        // never has to be edited when the store is rebuilt.
        return fetch('/products/' + PACK_HANDLE + '.js')
          .then(function (res) {
            return res.json();
          })
          .then(function (packProduct) {
            var packVariant = (packProduct.variants || [])[0];
            if (!packVariant) return null;

            var adds = [];
            var updates = {};
            overfull.forEach(function (line) {
              var packs = Math.floor(line.quantity / PACK_UNITS);
              adds.push({
                id: packVariant.id,
                quantity: packs,
                // Carry the colour the singles had, or the order says only
                // "5-Pack" and whoever packs the box has to guess.
                properties: line.variant_title ? { Color: line.variant_title } : {},
              });
              updates[line.key] = line.quantity % PACK_UNITS;
            });

            return postJson('/cart/update.js', { updates: updates })
              .then(function () {
                return postJson('/cart/add.js', { items: adds });
              })
              .then(function () {
                try {
                  sessionStorage.setItem(RAN_KEY, '1');
                } catch {
                  // No storage, no guard: the pass after the reload finds
                  // nothing overfull and stops on its own.
                }
                window.location.reload();
              });
          });
      })
      .catch(function (err) {
        // Never block the cart over this. The worst case is the buyer paying
        // singles prices, which is what they would have paid anyway.
        console.warn('[ruck] could not regroup Anvil packs', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
